'use client';

import { useRef, useEffect, useCallback, useState } from 'react';
import {
  motion,
  useInView,
  useAnimation,
  useScroll,
  useTransform,
  AnimatePresence,
  type Variants,
} from 'framer-motion';

/* ------------------------------------------------------------------ */
/*  ScrollStepCard — scroll-driven stacking card                       */
/* ------------------------------------------------------------------ */

/**
 * ScrollStepCard — dramatic scroll-driven stacking card.
 *
 * Expects exactly TWO children:
 *   1. Left side (text content)
 *   2. Right side (mockup/animation)
 *
 * Each half enters from its own direction with staggered timing,
 * blur clearing, and the card itself scales up with a purple glow.
 */
export function ScrollStepCard({
  children,
  index,
  className = '',
}: {
  children: React.ReactNode;
  index: number;
  className?: string;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'start 0.12'],
  });

  /* ---- Card container animations ---- */
  const cardY = useTransform(scrollYProgress, [0, 0.5, 1], [120, 20, 0]);
  const cardOpacity = useTransform(scrollYProgress, [0, 0.25, 0.5], [0, 0.5, 1]);
  const cardScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.88, 0.96, 1]);

  /* ---- Left side (text) — slides in from left ---- */
  const leftX = useTransform(scrollYProgress, [0, 0.4, 0.75], [-60, -10, 0]);
  const leftOpacity = useTransform(scrollYProgress, [0, 0.3, 0.6], [0, 0.4, 1]);

  /* ---- Right side (mockup) — slides in from right + scales ---- */
  const rightX = useTransform(scrollYProgress, [0, 0.45, 0.8], [80, 15, 0]);
  const rightOpacity = useTransform(scrollYProgress, [0, 0.35, 0.7], [0, 0.3, 1]);
  const rightScale = useTransform(scrollYProgress, [0, 0.5, 0.85], [0.75, 0.92, 1]);
  const rightRotateY = useTransform(scrollYProgress, [0, 0.5, 0.85], [-12, -3, 0]);

  /* ---- Purple glow shadow that intensifies ---- */
  const glowIntensity = useTransform(scrollYProgress, [0, 0.6, 1], [0, 0.08, 0.12]);

  const topValues = ['top-24', 'top-32', 'top-40', 'top-48'];
  const zValues = ['z-10', 'z-20', 'z-30', 'z-40'];

  // Convert children to array so we can wrap each half
  const childArray = Array.isArray(children) ? children : [children];

  return (
    <div
      ref={cardRef}
      className={`sticky ${topValues[index]} ${zValues[index]} mb-6 ${className}`}
      style={{ perspective: '1400px' }}
    >
      <motion.div
        style={{
          y: cardY,
          opacity: cardOpacity,
          scale: cardScale,
          boxShadow: useTransform(
            glowIntensity,
            (v) =>
              `0 ${25 * v * 10}px ${50 * v * 10}px rgba(124,58,237,${v}), 0 4px 20px rgba(0,0,0,${v * 0.5})`
          ),
        }}
        className="bg-gradient-to-br from-white via-white to-purple-50/60 rounded-2xl border border-gray-200 overflow-hidden will-change-transform"
      >
        <div className="grid md:grid-cols-2 gap-6 p-6 md:p-8">
          {/* Left: text — slides from left */}
          <motion.div
            style={{ x: leftX, opacity: leftOpacity }}
            className="flex flex-col justify-center will-change-transform"
          >
            {childArray[0]}
          </motion.div>

          {/* Right: mockup — slides from right with 3D tilt */}
          <motion.div
            style={{
              x: rightX,
              opacity: rightOpacity,
              scale: rightScale,
              rotateY: rightRotateY,
            }}
            className="will-change-transform"
          >
            {childArray[1]}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

function useLoopWhenVisible(intervalMs: number) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: false, amount: 0.3 });
  const controls = useAnimation();

  const restart = useCallback(() => {
    controls.set('hidden');
    controls.start('visible');
  }, [controls]);

  useEffect(() => {
    if (!isInView) return;
    restart();
    const id = setInterval(restart, intervalMs);
    return () => clearInterval(id);
  }, [isInView, intervalMs, restart]);

  return { ref, controls, isInView };
}

/* ------------------------------------------------------------------ */
/*  1. Upload — clean card mockup                                      */
/* ------------------------------------------------------------------ */

export function StepUploadAnimation({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: false, amount: 0.3 });
  const [fileIndex, setFileIndex] = useState(0);

  const files = [
    { label: 'PDF', color: 'bg-red-700', borderColor: 'border-red-200', name: 'birth_certificate.pdf' },
    { label: 'JPG', color: 'bg-blue-500', borderColor: 'border-blue-200', name: 'passport_scan.jpg' },
    { label: 'DOC', color: 'bg-indigo-500', borderColor: 'border-indigo-200', name: 'legal_contract.docx' },
  ];

  useEffect(() => {
    if (!isInView) return;
    setFileIndex(0);
    const id = setInterval(() => {
      setFileIndex((prev) => (prev + 1) % files.length);
    }, 2500);
    return () => clearInterval(id);
  }, [isInView, files.length]);

  const currentFile = files[fileIndex];

  return (
    <div ref={ref} className={`relative w-full h-full flex flex-col p-5 sm:p-8 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-[#9333ea] flex items-center justify-center shadow-sm">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 14V6M10 6L7 9M10 6L13 9" stroke="white" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <span className="text-base font-bold text-gray-800">Upload Document</span>
      </div>

      {/* Drop zone */}
      <div className="flex-1 border-2 border-dashed border-purple-300 rounded-2xl flex flex-col items-center justify-center relative bg-purple-50/40 min-h-0 overflow-hidden">
        {/* File type badges */}
        <div className="flex gap-2.5 mb-4">
          {files.map((f, i) => (
            <motion.div
              key={f.label}
              className={`${f.color} px-3 py-1 rounded-md text-xs font-bold text-white shadow-sm`}
              animate={{
                scale: i === fileIndex ? 1.2 : 1,
                opacity: i === fileIndex ? 1 : 0.65,
              }}
              transition={{ duration: 0.3 }}
            >
              {f.label}
            </motion.div>
          ))}
        </div>

        {/* Cycling file card */}
        <div className="relative h-[110px] w-[85px] mb-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={fileIndex}
              className="absolute inset-0 bg-white rounded-xl shadow-xl border border-gray-200"
              initial={{ y: -25, opacity: 0, scale: 0.8, rotate: -5 }}
              animate={{ y: 0, opacity: 1, scale: 1, rotate: 0 }}
              exit={{ y: 25, opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 200, damping: 16 }}
            >
              <div className="absolute top-0 right-0 w-6 h-6" style={{
                background: 'linear-gradient(135deg, #f3f4f6 50%, #e5e7eb 50%)',
                clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
              }} />
              <div className="p-3 pt-4 space-y-2">
                <div className="h-1 w-[80%] bg-gray-300 rounded-full" />
                <div className="h-0.5 w-[60%] bg-gray-200 rounded-full" />
                <div className="h-0.5 w-[90%] bg-gray-200 rounded-full" />
                <div className="h-0.5 w-[45%] bg-gray-200 rounded-full" />
                <div className="h-0.5 w-[70%] bg-gray-200 rounded-full" />
                <div className="h-0.5 w-[55%] bg-gray-200 rounded-full" />
              </div>
              <div className={`absolute -top-2 -left-2 px-2 py-0.5 ${currentFile.color} rounded-md text-[11px] font-bold text-white shadow-md`}>
                {currentFile.label}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="text-sm text-gray-600 font-semibold">Drag & drop or browse</div>
        <div className="text-xs text-gray-500 mt-1">PDF, Word, JPG, PNG, TIFF — up to 50 MB</div>
      </div>

      {/* Form fields — always visible */}
      <div className="grid grid-cols-3 gap-2 mt-3">
        {/* Category */}
        <div className="h-9 bg-white rounded-xl border border-gray-200 flex items-center px-3 shadow-sm">
          <span className="text-xs text-gray-500">Category</span>
          <svg className="ml-auto" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2.5 4L5 6.5L7.5 4" stroke="#9CA3AF" strokeWidth="1.2" /></svg>
        </div>
        {/* Language */}
        <div className="h-9 bg-white rounded-xl border border-gray-200 flex items-center px-3 shadow-sm gap-1.5 justify-center">
          <div className="px-1.5 py-0.5 rounded bg-purple-100 flex items-center justify-center">
            <span className="text-[10px] text-purple-800 font-bold">ES</span>
          </div>
          <svg width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M1 5H11M11 5L8 2M11 5L8 8" stroke="#9333ea" strokeWidth="1.2" strokeLinecap="round" /></svg>
          <div className="px-1.5 py-0.5 rounded bg-purple-100 flex items-center justify-center">
            <span className="text-[10px] text-purple-800 font-bold">EN</span>
          </div>
        </div>
        {/* Assign team member */}
        <div className="h-9 bg-white rounded-xl border border-gray-200 flex items-center px-3 shadow-sm gap-1.5">
          <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="4" r="2.5" stroke="#7c3aed" strokeWidth="1.2" />
              <path d="M1.5 11C1.5 8.5 3.5 6.5 6 6.5S10.5 8.5 10.5 11" stroke="#7c3aed" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
          </div>
          <span className="text-xs text-gray-500">Assign</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  2. OCR Processing                                                  */
/* ------------------------------------------------------------------ */

export function StepOCRAnimation({ className = '' }: { className?: string }) {
  const { ref, controls, isInView } = useLoopWhenVisible(7000);

  // Phase 1: scanning (0s - 2.5s)
  const scanBeamVariants: Variants = {
    hidden: { top: '5%', opacity: 0 },
    visible: {
      top: ['5%', '90%'],
      opacity: [0, 1, 1, 0],
      transition: { duration: 2.2, ease: 'easeInOut' },
    },
  };

  const progressVariants: Variants = {
    hidden: { width: '0%' },
    visible: {
      width: '100%',
      transition: { duration: 2, ease: 'easeInOut' },
    },
  };

  // Phase 2: compare view appears (2.5s+)
  const compareVariants: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1, y: 0,
      transition: { delay: 2.8, duration: 0.5, ease: 'easeOut' },
    },
  };

  const editLineVariants: Variants = {
    hidden: { opacity: 0, scaleX: 0 },
    visible: (i: number) => ({
      opacity: 1, scaleX: 1,
      transition: { delay: 3.2 + i * 0.25, duration: 0.3, ease: 'easeOut' },
    }),
  };

  const cursorVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delay: 4.5, duration: 0.2 },
    },
  };

  return (
    <div ref={ref} className={`relative w-full h-full flex flex-col ${className}`}>
      {/* Phase 1: Scanning overlay — fades out after scan */}
      <motion.div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 rounded-2xl"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 40%, #ede9fe 100%)', pointerEvents: 'none' }}
        variants={{
          hidden: { opacity: 1 },
          visible: { opacity: [1, 1, 0], transition: { duration: 3, times: [0, 0.8, 1] } },
        }}
        initial="hidden"
        animate={controls}
      >
        {/* Document being scanned */}
        <div className="w-44 bg-white rounded-2xl border border-gray-200 shadow-xl relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
          <div className="p-4 space-y-3">
            <div className="h-2.5 w-[65%] bg-gray-300 rounded-full" />
            <div className="h-2 w-[85%] bg-gray-200 rounded-full" />
            <div className="h-2 w-[55%] bg-gray-200 rounded-full" />
            <div className="h-2 w-[75%] bg-gray-200 rounded-full" />
            <div className="mt-3 h-2.5 w-[50%] bg-gray-300 rounded-full" />
            <div className="h-2 w-[90%] bg-gray-200 rounded-full" />
            <div className="h-2 w-[45%] bg-gray-200 rounded-full" />
          </div>
          <motion.div
            className="absolute left-0 right-0 h-1"
            style={{
              background: 'linear-gradient(90deg, transparent, #9333ea, #9333ea, transparent)',
              boxShadow: '0 0 20px 5px rgba(124,58,237,0.4)',
            }}
            variants={scanBeamVariants}
            initial="hidden"
            animate={controls}
          />
        </div>
        <div className="text-xl font-bold text-gray-800">Processing OCR</div>
        <div className="w-56">
          <div className="h-2.5 bg-purple-100 rounded-full overflow-hidden">
            <motion.div className="h-full bg-[#9333ea] rounded-full" variants={progressVariants} initial="hidden" animate={controls} />
          </div>
          <div className="text-sm text-gray-500 text-center mt-1.5">Extracting text...</div>
        </div>
      </motion.div>

      {/* Phase 2: Compare view — original vs reconstructed */}
      <motion.div
        className="w-full h-full flex flex-col"
        variants={compareVariants}
        initial="hidden"
        animate={controls}
      >
        {/* Header bar */}
        <div className="h-10 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0 rounded-t-xl">
          <div className="text-sm font-semibold text-gray-700">OCR Review</div>
          <div className="flex gap-2 items-center">
            <div className="bg-green-50 border border-green-200 rounded-lg px-2.5 py-1 flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-xs font-semibold text-green-700">98% accuracy</span>
            </div>
            <div className="h-7 px-3 bg-[#9333ea] rounded-lg text-xs text-white flex items-center font-bold shadow-sm">Edit</div>
          </div>
        </div>

        {/* Side by side: Original vs Reconstructed */}
        <div className="flex-1 flex min-h-0">
          {/* Left: Original scan */}
          <div className="w-1/2 border-r border-gray-200 p-4 pb-5 bg-gray-50/70 overflow-hidden flex flex-col">
            <div className="text-xs text-gray-500 font-semibold mb-2 uppercase tracking-wide">Original Scan</div>
            <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="h-2.5 w-[70%] bg-gray-300 rounded-full" />
                <div className="h-2 w-[90%] bg-gray-200 rounded-full" />
                <div className="h-2 w-[55%] bg-gray-200 rounded-full" />
                <div className="h-2 w-[80%] bg-gray-200 rounded-full" />
              </div>
              <div className="space-y-3">
                <div className="h-2.5 w-[50%] bg-gray-300 rounded-full" />
                <div className="h-2 w-[85%] bg-gray-200 rounded-full" />
                <div className="h-2 w-[45%] bg-gray-200 rounded-full" />
                <div className="h-2 w-[75%] bg-gray-200 rounded-full" />
              </div>
              <div className="space-y-3">
                <div className="h-2 w-[65%] bg-gray-200 rounded-full" />
                <div className="h-2 w-[90%] bg-gray-200 rounded-full" />
                <div className="h-2 w-[40%] bg-gray-200 rounded-full" />
              </div>
            </div>
          </div>

          {/* Right: Reconstructed / editable */}
          <div className="w-1/2 p-4 pb-5 bg-white overflow-hidden flex flex-col">
            <div className="text-xs text-gray-500 font-semibold mb-2 uppercase tracking-wide flex items-center gap-1.5">
              Editable Preview
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M8.5 1.5L10.5 3.5L4 10H2V8L8.5 1.5Z" stroke="#9333ea" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="flex-1 bg-purple-50/50 rounded-xl border border-purple-200 shadow-sm p-4 relative flex flex-col justify-between">
              {/* Editable text lines appearing one by one — 3 blocks */}
              <div className="space-y-3">
                {[
                  { w: '70%', color: 'bg-purple-400' },
                  { w: '90%', color: 'bg-purple-300' },
                  { w: '55%', color: 'bg-purple-300' },
                  { w: '80%', color: 'bg-purple-300' },
                ].map((line, i) => (
                  <motion.div
                    key={i}
                    className={`h-2.5 ${line.color} rounded-full origin-left`}
                    style={{ width: line.w }}
                    custom={i}
                    variants={editLineVariants}
                    initial="hidden"
                    animate={controls}
                  />
                ))}
              </div>
              <div className="space-y-3">
                {[
                  { w: '50%', color: 'bg-purple-400' },
                  { w: '85%', color: 'bg-purple-300' },
                  { w: '45%', color: 'bg-purple-300' },
                  { w: '75%', color: 'bg-purple-300' },
                ].map((line, i) => (
                  <motion.div
                    key={`b-${i}`}
                    className={`h-2 ${line.color} rounded-full origin-left`}
                    style={{ width: line.w }}
                    custom={i + 4}
                    variants={editLineVariants}
                    initial="hidden"
                    animate={controls}
                  />
                ))}
              </div>
              <div className="space-y-3">
                {[
                  { w: '65%', color: 'bg-purple-300' },
                  { w: '90%', color: 'bg-purple-300' },
                  { w: '40%', color: 'bg-purple-300' },
                ].map((line, i) => (
                  <motion.div
                    key={`c-${i}`}
                    className={`h-2 ${line.color} rounded-full origin-left`}
                    style={{ width: line.w }}
                    custom={i + 8}
                    variants={editLineVariants}
                    initial="hidden"
                    animate={controls}
                  />
                ))}
              </div>

              {/* Blinking cursor */}
              <motion.div
                className="absolute"
                style={{ top: '38%', left: '58%' }}
                variants={cursorVariants}
                initial="hidden"
                animate={controls}
              >
                <motion.div
                  className="w-[2px] h-5 bg-[#9333ea] rounded-full"
                  animate={isInView ? { opacity: [1, 0, 1] } : {}}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear', repeatType: 'reverse' }}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  3. Translate & Edit — side-by-side                                */
/* ------------------------------------------------------------------ */

export function StepTranslateAnimation({ className = '' }: { className?: string }) {
  const { ref, controls, isInView } = useLoopWhenVisible(6000);

  const sourceBlockVariants: Variants = {
    hidden: { opacity: 0, y: 6 },
    visible: (i: number) => ({
      opacity: 1, y: 0,
      transition: { delay: 0.3 + i * 0.15, duration: 0.3, ease: 'easeOut' },
    }),
  };

  const translatedBlockVariants: Variants = {
    hidden: { opacity: 0, scaleX: 0 },
    visible: (i: number) => ({
      opacity: 1, scaleX: 1,
      transition: { delay: 1.2 + i * 0.4, duration: 0.4, ease: 'easeOut' },
    }),
  };

  const aiBadgeVariants: Variants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1, opacity: 1,
      transition: { delay: 0.8, type: 'spring', stiffness: 300, damping: 15 },
    },
  };

  const sourceBlocks = [
    { lines: ['85%', '70%', '90%'] },
    { lines: ['60%', '80%'] },
    { lines: ['75%', '65%', '50%'] },
  ];

  const translatedBlocks = [
    { lines: ['80%', '75%', '85%'] },
    { lines: ['70%', '65%'] },
    { lines: ['90%', '55%', '60%'] },
  ];

  return (
    <div ref={ref} className={`relative w-full h-full flex flex-col ${className}`}>
      {/* Toolbar */}
      <div className="h-11 bg-white border-b border-gray-200 flex items-center justify-between px-5 shrink-0 rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-500 font-semibold">Document Review</div>
        </div>
        <div className="flex gap-2">
          <div className="px-3 h-7 bg-purple-100 rounded-lg text-xs text-purple-800 flex items-center font-bold border border-purple-200">Option 1</div>
          <div className="px-3 h-7 bg-gray-100 rounded-lg text-xs text-gray-600 flex items-center border border-gray-200">Option 2</div>
        </div>
        <div className="h-7 px-3 bg-green-600 rounded-lg text-xs text-white flex items-center font-bold shadow-sm">Finish</div>
      </div>

      {/* Side by side */}
      <div className="flex-1 flex min-h-0 relative">
        {/* Source (ES) */}
        <div className="w-1/2 border-r border-gray-200 p-4 overflow-hidden bg-gray-50/70">
          <div className="text-sm text-gray-500 font-semibold mb-3 flex items-center gap-2">
            <div className="px-2 py-0.5 rounded-md bg-purple-100 flex items-center justify-center">
              <span className="text-xs text-purple-800 font-bold">ES</span>
            </div>
            Original
          </div>
          <div className="space-y-3">
            {sourceBlocks.map((block, bi) => (
              <motion.div
                key={bi}
                className="bg-white rounded-xl p-3 space-y-2 border border-gray-200 shadow-sm"
                custom={bi}
                variants={sourceBlockVariants}
                initial="hidden"
                animate={controls}
              >
                {block.lines.map((w, li) => (
                  <div key={li} className="h-1.5 bg-gray-300 rounded-full" style={{ width: w }} />
                ))}
              </motion.div>
            ))}
          </div>
        </div>

        {/* AI sparkle in center */}
        <motion.div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
          variants={aiBadgeVariants}
          initial="hidden"
          animate={controls}
        >
          <motion.div
            className="w-10 h-10 rounded-full bg-[#9333ea] flex items-center justify-center shadow-lg"
            animate={isInView ? {
              boxShadow: [
                '0 0 0px rgba(124,58,237,0)',
                '0 0 18px rgba(124,58,237,0.5)',
                '0 0 0px rgba(124,58,237,0)',
              ],
            } : {}}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 1L10.8 7.2L17 9L10.8 10.8L9 17L7.2 10.8L1 9L7.2 7.2L9 1Z" fill="white" />
            </svg>
          </motion.div>
        </motion.div>

        {/* Translated (EN) */}
        <div className="w-1/2 p-4 overflow-hidden bg-white">
          <div className="text-sm text-gray-500 font-semibold mb-3 flex items-center gap-2">
            <div className="px-2 py-0.5 rounded-md bg-green-100 flex items-center justify-center">
              <span className="text-xs text-green-800 font-bold">EN</span>
            </div>
            Translation
          </div>
          <div className="space-y-3">
            {translatedBlocks.map((block, bi) => (
              <motion.div
                key={bi}
                className="bg-green-50 rounded-xl p-3 space-y-2 border border-green-200 shadow-sm origin-left"
                custom={bi}
                variants={translatedBlockVariants}
                initial="hidden"
                animate={controls}
              >
                {block.lines.map((w, li) => (
                  <div key={li} className="h-1.5 bg-green-300 rounded-full" style={{ width: w }} />
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  4. Export & Certify — finish modal                                */
/* ------------------------------------------------------------------ */

export function StepExportAnimation({ className = '' }: { className?: string }) {
  const { ref, controls, isInView } = useLoopWhenVisible(8000);

  // Phase 1: Toolbar from step 3 with cursor clicking Finish (0s–2s)
  const toolbarVariants: Variants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: [1, 1, 0],
      transition: { duration: 2.8, times: [0, 0.85, 1] },
    },
  };

  const cursorVariants: Variants = {
    hidden: { x: 0, y: 0, opacity: 0 },
    visible: {
      x: [0, 0, 0],
      y: [20, 0, 0],
      opacity: [0, 1, 1],
      scale: [1, 1, 0.85],
      transition: { duration: 1.8, times: [0, 0.4, 0.7], ease: 'easeInOut' },
    },
  };

  const finishGlowVariants: Variants = {
    hidden: { scale: 1 },
    visible: {
      scale: [1, 1, 0.92, 1.05, 1],
      transition: { duration: 2.2, times: [0, 0.5, 0.6, 0.7, 0.8], ease: 'easeInOut' },
    },
  };

  // Phase 2: Modal appears (2.5s+)
  const modalVariants: Variants = {
    hidden: { scale: 0.85, opacity: 0, y: 12 },
    visible: {
      scale: 1, opacity: 1, y: 0,
      transition: { delay: 2.8, type: 'spring', stiffness: 200, damping: 18 },
    },
  };

  const stampVariants: Variants = {
    hidden: { scale: 3, opacity: 0, rotate: -20 },
    visible: {
      scale: 1, opacity: 1, rotate: 0,
      transition: { delay: 4.2, type: 'spring', stiffness: 300, damping: 12 },
    },
  };

  const detailVariants: Variants = {
    hidden: { opacity: 0 },
    visible: (i: number) => ({
      opacity: 1,
      transition: { delay: 3.3 + i * 0.2, duration: 0.3 },
    }),
  };

  const formatVariants: Variants = {
    hidden: { opacity: 0, y: 4 },
    visible: {
      opacity: 1, y: 0,
      transition: { delay: 4, duration: 0.4, ease: 'easeOut' },
    },
  };

  const btnVariants: Variants = {
    hidden: { opacity: 0, y: 6 },
    visible: {
      opacity: 1, y: 0,
      transition: { delay: 4.8, duration: 0.4, ease: 'easeOut' },
    },
  };

  const details = [
    { label: 'Document', value: 'birth_cert.pdf' },
    { label: 'Languages', value: 'ES → EN' },
    { label: 'Credits', value: '1 per page', highlight: true },
  ];

  return (
    <div ref={ref} className={`relative w-full h-full flex flex-col ${className}`}>

      {/* Phase 1: Mini toolbar from step 3 + cursor clicking Finish */}
      <motion.div
        className="absolute inset-0 z-20 flex flex-col rounded-2xl"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 40%, #ede9fe 100%)', pointerEvents: 'none' }}
        variants={toolbarVariants}
        initial="hidden"
        animate={controls}
      >
        {/* Toolbar — same as step 3 final state */}
        <div className="h-11 bg-white border-b border-gray-200 flex items-center justify-between px-5 shrink-0 rounded-t-xl">
          <div className="flex items-center gap-2">
            <div className="text-sm text-gray-500 font-semibold">Document Review</div>
          </div>
          <div className="flex gap-2 items-center">
            <div className="px-3 h-7 bg-purple-100 rounded-lg text-xs text-purple-800 flex items-center font-bold border border-purple-200">Option 1</div>
            <div className="px-3 h-7 bg-gray-100 rounded-lg text-xs text-gray-600 flex items-center border border-gray-200">Option 2</div>
            <motion.div
              className="relative"
              variants={finishGlowVariants}
              initial="hidden"
              animate={controls}
            >
              <div className="h-7 px-4 bg-green-600 rounded-lg text-xs text-white flex items-center font-bold shadow-sm">
                Finish
              </div>
              {/* Click ripple */}
              <motion.div
                className="absolute inset-0 rounded-lg bg-white/30"
                variants={{
                  hidden: { opacity: 0, scale: 1 },
                  visible: {
                    opacity: [0, 0, 0.5, 0],
                    scale: [1, 1, 1.3, 1.5],
                    transition: { duration: 2.2, times: [0, 0.55, 0.65, 0.8] },
                  },
                }}
                initial="hidden"
                animate={controls}
              />
            </motion.div>
          </div>
        </div>

        {/* Side-by-side content — step 3 final state (translation complete) */}
        <div className="flex-1 flex min-h-0 relative">
          {/* Source (ES) */}
          <div className="w-1/2 border-r border-gray-200 p-3 overflow-hidden bg-gray-50/70">
            <div className="text-xs text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-600 text-[10px] font-bold">ES</span>
              Original
            </div>
            <div className="space-y-2">
              <div className="bg-white rounded-lg p-2 space-y-1.5 border border-gray-200">
                <div className="h-1.5 w-[85%] bg-gray-300 rounded-full" />
                <div className="h-1 w-[70%] bg-gray-200 rounded-full" />
                <div className="h-1 w-[90%] bg-gray-200 rounded-full" />
              </div>
              <div className="bg-white rounded-lg p-2 space-y-1.5 border border-gray-200">
                <div className="h-1.5 w-[60%] bg-gray-300 rounded-full" />
                <div className="h-1 w-[80%] bg-gray-200 rounded-full" />
              </div>
              <div className="bg-white rounded-lg p-2 space-y-1.5 border border-gray-200">
                <div className="h-1.5 w-[75%] bg-gray-300 rounded-full" />
                <div className="h-1 w-[65%] bg-gray-200 rounded-full" />
              </div>
            </div>
          </div>

          {/* AI sparkle in center */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="w-8 h-8 rounded-full bg-[#9333ea] flex items-center justify-center shadow-lg">
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                <path d="M9 1L10.8 7.2L17 9L10.8 10.8L9 17L7.2 10.8L1 9L7.2 7.2L9 1Z" fill="white" />
              </svg>
            </div>
          </div>

          {/* Translated (EN) — all blocks visible = completed */}
          <div className="w-1/2 p-3 overflow-hidden bg-white">
            <div className="text-xs text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-600 text-[10px] font-bold">EN</span>
              Translation ✓
            </div>
            <div className="space-y-2">
              <div className="bg-green-50 rounded-lg p-2 space-y-1.5 border border-green-200">
                <div className="h-1.5 w-[80%] bg-green-300 rounded-full" />
                <div className="h-1 w-[75%] bg-green-200 rounded-full" />
                <div className="h-1 w-[85%] bg-green-200 rounded-full" />
              </div>
              <div className="bg-green-50 rounded-lg p-2 space-y-1.5 border border-green-200">
                <div className="h-1.5 w-[70%] bg-green-300 rounded-full" />
                <div className="h-1 w-[65%] bg-green-200 rounded-full" />
              </div>
              <div className="bg-green-50 rounded-lg p-2 space-y-1.5 border border-green-200">
                <div className="h-1.5 w-[90%] bg-green-300 rounded-full" />
                <div className="h-1 w-[55%] bg-green-200 rounded-full" />
              </div>
            </div>
          </div>
        </div>

        {/* Cursor pointer — positioned to point at the Finish button */}
        <motion.div
          className="absolute z-30"
          style={{ right: '16px', top: '10px' }}
          variants={cursorVariants}
          initial="hidden"
          animate={controls}
        >
          <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
            <path d="M1 1L1 16L5.5 12L9 19L12 17.5L8.5 10.5L14 10L1 1Z" fill="black" stroke="white" strokeWidth="1.5" />
          </svg>
        </motion.div>
      </motion.div>

      {/* Phase 2: Dimmed bg + modal */}
      <motion.div
        className="absolute inset-0 rounded-2xl bg-black/5 z-10"
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 2.6 } } }}
        initial="hidden"
        animate={controls}
      />

      <div className="relative w-full h-full flex items-center justify-center p-5 sm:p-8">
        <motion.div
          className="relative z-10 w-full max-w-[320px] bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden"
          variants={modalVariants}
          initial="hidden"
          animate={controls}
        >
          {/* Header */}
          <div className="p-5 pb-3 flex flex-col items-center">
            <div className="text-lg font-bold text-gray-900">Order Complete</div>
            <div className="text-xs text-gray-500 mt-1">Your translation is ready</div>
          </div>

          {/* Details */}
          <div className="px-5 space-y-2 pb-3">
            {details.map((d, i) => (
              <motion.div
                key={i}
                className={`flex items-center justify-between py-2 px-3 rounded-xl ${
                  d.highlight ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50'
                }`}
                custom={i}
                variants={detailVariants}
                initial="hidden"
                animate={controls}
              >
                <span className="text-sm text-gray-500">{d.label}</span>
                <span className={`text-sm font-bold ${d.highlight ? 'text-purple-600' : 'text-gray-700'}`}>{d.value}</span>
              </motion.div>
            ))}
          </div>

          {/* Format selector */}
          <motion.div className="px-5 pb-2" variants={formatVariants} initial="hidden" animate={controls}>
            <div className="text-xs text-gray-500 font-semibold mb-1.5">Download format</div>
            <div className="flex gap-2">
              <div className="flex-1 h-9 bg-red-50 border-2 border-red-300 rounded-xl flex items-center justify-center gap-1.5 shadow-sm">
                <div className="px-1.5 py-0.5 bg-red-700 rounded text-[9px] font-bold text-white">PDF</div>
                <span className="text-xs font-semibold text-red-700">PDF</span>
              </div>
              <div className="flex-1 h-9 bg-white border border-gray-200 rounded-xl flex items-center justify-center gap-1.5">
                <div className="px-1.5 py-0.5 bg-indigo-500 rounded text-[9px] font-bold text-white">DOC</div>
                <span className="text-xs font-medium text-gray-500">Word</span>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <div className="w-4 h-4 rounded border-2 border-green-400 bg-green-50 flex items-center justify-center">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5L4.5 7L8 3" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="text-xs text-gray-600">Include certificate</span>
            </div>
          </motion.div>

          {/* Download button */}
          <motion.div className="px-5 pb-5 pt-2" variants={btnVariants} initial="hidden" animate={controls}>
            <motion.div
              className="w-full h-10 bg-green-500 rounded-xl flex items-center justify-center gap-2 cursor-pointer"
              animate={isInView ? { scale: [1, 1.02, 1] } : {}}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 5.5 }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3V11M8 11L5 8M8 11L11 8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                <path d="M3 13H13" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span className="text-sm font-bold text-white">Download</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  StickyStepsSection — left scrolls, right stays sticky              */
/* ------------------------------------------------------------------ */

interface Step {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  animation: React.ReactNode;
}

interface StickyStepsSectionProps {
  steps: Step[];
}

export function StickyStepsSection({ steps }: StickyStepsSectionProps) {
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      let bestIndex = 0;
      let bestDistance = Infinity;

      stepRefs.current.forEach((ref, index) => {
        if (!ref) return;
        const rect = ref.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const target = window.innerHeight * 0.45;
        const distance = Math.abs(center - target);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      setActiveIndex(bestIndex);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [steps.length]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">

        {/* ---- Left side: scrolling step blocks ---- */}
        <div className="w-full lg:w-[38%]">
          {steps.map((step, index) => {
            const isActive = index === activeIndex;

            return (
              <div
                key={step.number}
                ref={(el) => { stepRefs.current[index] = el; }}
                className="py-8 lg:py-14 min-h-0 lg:min-h-[50vh]"
              >
                <motion.div
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Step number + icon */}
                  <div className="flex items-center gap-3 mb-4">
                    <motion.div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-md"
                      animate={{
                        backgroundColor: isActive ? '#9333ea' : '#e5e7eb',
                        color: isActive ? '#ffffff' : '#6b7280',
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </motion.div>
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-purple-50">
                      {step.icon}
                    </div>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-base sm:text-lg leading-relaxed text-gray-600 max-w-md">
                    {step.description}
                  </p>
                </motion.div>

                {/* Mobile only: show animation inline */}
                <div className="lg:hidden mt-6">
                  <div
                    className="w-full rounded-2xl overflow-hidden ring-1 ring-black/5"
                    style={{
                      aspectRatio: '4 / 4',
                      background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 40%, #ede9fe 100%)',
                      boxShadow: '0 20px 50px -12px rgba(124,58,237,0.15), 0 8px 24px -8px rgba(0,0,0,0.08)',
                    }}
                  >
                    {step.animation}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ---- Right side: sticky animation panel ---- */}
        <div className="hidden lg:block lg:w-[62%]">
          <div
            className="sticky top-24"
            style={{ height: 'fit-content' }}
          >
            <div
              className="relative w-full rounded-2xl overflow-hidden ring-1 ring-black/5"
              style={{
                aspectRatio: '5 / 3.5',
                background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 40%, #ede9fe 100%)',
                boxShadow: '0 20px 50px -12px rgba(124,58,237,0.15), 0 8px 24px -8px rgba(0,0,0,0.08)',
              }}
            >
              <AnimatePresence initial={false}>
                <motion.div
                  key={activeIndex}
                  className="absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                >
                  {steps[activeIndex]?.animation}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
